import { isSSR } from '@front10/utils';
import type Leaflet from 'leaflet';
import VectorMapMarker from '../markers/VectorMapMarker';
import React from 'react';
import { MarkerData } from '../classes/MarkerData';
import {
  isPointContainedInBounds,
  isValidLocation,
  isPointShowMultipleTimes,
  getMapsWhereIsShowPoint,
  IS_SHOW_POINT
} from './../helpers/helper';

const isClusterContainedInBounds = (
  lat: number,
  long: number,
  mapRef: any,
  useReducerBound: boolean,
  cyclicMapPaddingWidth: number
) => {
  return isPointContainedInBounds(lat, long, mapRef, useReducerBound, cyclicMapPaddingWidth);
};

const createClusterCustomIcon = (
  counter: any,
  clusterSizesKeys: string[],
  clusterSizes: any,
  classNameCluster: string
) => {
  let count = counter;
  if (typeof counter === 'object') {
    count = counter.getChildCount();
  }
  let size = '';
  clusterSizesKeys.forEach((key) => {
    if (count >= clusterSizes[key].min && count <= clusterSizes[key].max) {
      size = key;
    }
  });

  const options = {
    cluster: `markerCluster${size}`,
    circle4: `markerCluster${size}DivFour`,
    label: `markerCluster${size}Label`
  };

  if (!isSSR()) {
    const leaflet = require('leaflet') as typeof Leaflet;
    return leaflet.divIcon({
      html: `<div class="${options.circle4} ${classNameCluster}">
          <span class="${options.label}">${count}</span>
        </div>`,
      className: `marker-cluster-point ${options.cluster}`
    });
  }
  return null;
};

export const renderClusterFn = ({
  clusters,
  clusterSizesKeys,
  clusterSizes,
  supercluster,
  maxZoom,
  mapRef,
  enableHoverShapeCluster,
  setPolygon,
  enableCluster,
  handleSetMarkerHover,
  isShowTopOffers,
  mapProps,
  markerHover,
  zIndex,
  setZIndex,
  hidePointWithUserFilter,
  fareMarkers,
  markersSettings,
  classNameCluster,
  cyclicMapPaddingWidth
}: any) => {
  const { Marker } = require('react-leaflet');

  return clusters.map((cluster: any) => {
    // every cluster point has coordinates
    const [longitude, latitude] = cluster.geometry.coordinates;
    // the point may be either a cluster or a crime point
    const { cluster: isCluster, point_count: pointCount } = cluster.properties;
    const basicClusterProps: any = {
      key: `cluster-${cluster.id}`,
      position: [latitude, longitude],
      icon: createClusterCustomIcon(pointCount, clusterSizesKeys, clusterSizes, classNameCluster),
      onClick: () => {
        const expansionZoom = Math.min(supercluster.getClusterExpansionZoom(cluster.id), maxZoom);
        const leaflet = mapRef.current?.leafletElement;
        leaflet?.setView([latitude, longitude], expansionZoom, {
          animate: true
        });
        if (enableHoverShapeCluster) {
          setPolygon([]);
        }
      },
      onMouseOver: () =>
        enableHoverShapeCluster &&
        setPolygon(
          supercluster
            .getChildren(cluster.id)
            .map((item: { geometry: { coordinates: any[] } }) => [
              item.geometry.coordinates[1],
              item.geometry.coordinates[0]
            ])
        ),
      onMouseOut: () => enableHoverShapeCluster && setPolygon([])
    };
    // we have a cluster to render
    if (isCluster && enableCluster) {
      const useReducerBound = isPointShowMultipleTimes(latitude, longitude, mapRef);
      const clustersInBounds = [
        isClusterContainedInBounds(
          latitude,
          longitude,
          mapRef,
          useReducerBound,
          cyclicMapPaddingWidth
        ) ? (
          <Marker {...basicClusterProps} />
        ) : null,
        latitude &&
        longitude &&
        isClusterContainedInBounds(
          latitude,
          longitude - 360,
          mapRef,
          useReducerBound,
          cyclicMapPaddingWidth
        ) ? (
          <Marker
            {...basicClusterProps}
            key={`cluster-${cluster.id}-map-1`}
            position={[latitude, longitude - 360]}
            onClick={() => {
              const expansionZoom = Math.min(
                supercluster.getClusterExpansionZoom(cluster.id),
                maxZoom
              );
              const leaflet = mapRef.current?.leafletElement;
              leaflet?.setView([latitude, longitude - 360], expansionZoom, {
                animate: true
              });
              if (enableHoverShapeCluster) {
                setPolygon([]);
              }
            }}
          />
        ) : null,
        latitude &&
        longitude &&
        isClusterContainedInBounds(
          latitude,
          longitude + 360,
          mapRef,
          useReducerBound,
          cyclicMapPaddingWidth
        ) ? (
          <Marker
            {...basicClusterProps}
            key={`cluster-${cluster.id}-map-2`}
            position={[latitude, longitude + 360]}
            onClick={() => {
              const expansionZoom = Math.min(
                supercluster.getClusterExpansionZoom(cluster.id),
                maxZoom
              );
              const leaflet = mapRef.current?.leafletElement;
              leaflet?.setView([latitude, longitude + 360], expansionZoom, {
                animate: true
              });
              if (enableHoverShapeCluster) {
                setPolygon([]);
              }
            }}
          />
        ) : null
      ];
      for (let index = 0; index < clustersInBounds.length; index++) {
        if (clustersInBounds[index]) {
          return clustersInBounds[index];
        }
      }
      const mapsWhenIsShowPoint = getMapsWhereIsShowPoint(latitude, longitude, mapRef, 0);
      if (mapsWhenIsShowPoint[0] === IS_SHOW_POINT) {
        return <Marker {...basicClusterProps} />;
      } else if (mapsWhenIsShowPoint[1] === IS_SHOW_POINT) {
        return (
          <Marker
            {...basicClusterProps}
            key={`cluster-${cluster.id}-map-1`}
            position={[latitude, longitude - 360]}
            onClick={() => {
              const expansionZoom = Math.min(
                supercluster.getClusterExpansionZoom(cluster.id),
                maxZoom
              );
              const leaflet = mapRef.current?.leafletElement;
              leaflet?.setView([latitude, longitude - 360], expansionZoom, {
                animate: true
              });
              if (enableHoverShapeCluster) {
                setPolygon([]);
              }
            }}
          />
        );
      } else if (mapsWhenIsShowPoint[2] === IS_SHOW_POINT) {
        return (
          <Marker
            {...basicClusterProps}
            key={`cluster-${cluster.id}-map-2`}
            position={[latitude, longitude + 360]}
            onClick={() => {
              const expansionZoom = Math.min(
                supercluster.getClusterExpansionZoom(cluster.id),
                maxZoom
              );
              const leaflet = mapRef.current?.leafletElement;
              leaflet?.setView([latitude, longitude + 360], expansionZoom, {
                animate: true
              });
              if (enableHoverShapeCluster) {
                setPolygon([]);
              }
            }}
          />
        );
      } else {
        return <Marker {...basicClusterProps} />;
      }
    }
    //INFO: Avoiding double rendering on fares that have data
    if (cluster.properties.markerType && cluster.properties.markerType !== 'fare') {
      const useReducerBound_ = isPointShowMultipleTimes(
        cluster.properties.location[0],
        cluster.properties.location[1],
        mapRef
      );
      const basicProps: any = {
        onMouseOver: handleSetMarkerHover,
        showTopOffers: isShowTopOffers,
        key: `marker-${cluster.properties.id}`,
        marker: cluster.properties,
        isMapMode: !!mapProps?.mapMode?.mode,
        markerHover: markerHover,
        mapRef: mapRef,
        zIndex: zIndex,
        setZIndex: setZIndex
      };
      const clustersInBounds_ = [
        isClusterContainedInBounds(
          cluster.properties.location[0],
          cluster.properties.location[1],
          mapRef,
          useReducerBound_,
          cyclicMapPaddingWidth
        ) ? (
          <VectorMapMarker {...basicProps} {...markersSettings} />
        ) : null,
        isValidLocation(cluster?.properties?.location) &&
        isClusterContainedInBounds(
          cluster.properties.location[0],
          cluster.properties.location[1] - 360,
          mapRef,
          useReducerBound_,
          cyclicMapPaddingWidth
        ) ? (
          <VectorMapMarker
            {...basicProps}
            {...markersSettings}
            key={`marker-${cluster.properties.id}-map-1`}
            marker={{
              ...cluster.properties,
              location: [cluster.properties.location[0], cluster.properties.location[1] - 360]
            }}
          />
        ) : null,
        isValidLocation(cluster?.properties?.location) &&
        isClusterContainedInBounds(
          cluster.properties.location[0],
          cluster.properties.location[1] + 360,
          mapRef,
          useReducerBound_,
          cyclicMapPaddingWidth
        ) ? (
          <VectorMapMarker
            {...basicProps}
            {...markersSettings}
            key={`marker-${cluster.properties.id}-map-2`}
            marker={{
              ...cluster.properties,
              location: [cluster.properties.location[0], cluster.properties.location[1] + 360]
            }}
          />
        ) : null
      ];
      //INFO: https://trello.com/c/xbM4mNy0
      if (hidePointWithUserFilter) {
        if (
          fareMarkers.find(
            (fareMarker: MarkerData) => fareMarker.fare.destination === cluster.properties.value
          )
        ) {
          for (let index = 0; index < clustersInBounds_.length; index++) {
            if (clustersInBounds_[index]) {
              return clustersInBounds_[index];
            }
          }
          const mapsWhenIsShowPoint_ = getMapsWhereIsShowPoint(
            cluster.properties.location[0],
            cluster.properties.location[1],
            mapRef,
            0
          );
          if (mapsWhenIsShowPoint_[0] === IS_SHOW_POINT) {
            return <VectorMapMarker {...basicProps} {...markersSettings} />;
          } else if (mapsWhenIsShowPoint_[1] === IS_SHOW_POINT) {
            return (
              <VectorMapMarker
                {...basicProps}
                {...markersSettings}
                key={`marker-${cluster.properties.id}-map-1`}
                marker={{
                  ...cluster.properties,
                  location: [cluster.properties.location[0], cluster.properties.location[1] - 360]
                }}
              />
            );
          } else if (mapsWhenIsShowPoint_[2] === IS_SHOW_POINT) {
            return (
              <VectorMapMarker
                {...basicProps}
                {...markersSettings}
                key={`marker-${cluster.properties.id}-map-2`}
                marker={{
                  ...cluster.properties,
                  location: [cluster.properties.location[0], cluster.properties.location[1] + 360]
                }}
              />
            );
          } else {
            return <VectorMapMarker {...basicProps} {...markersSettings} />;
          }
        }
      } else {
        for (let index = 0; index < clustersInBounds_.length; index++) {
          if (clustersInBounds_[index]) {
            return clustersInBounds_[index];
          }
        }
        const _mapsWhenIsShowPoint = getMapsWhereIsShowPoint(
          cluster.properties.location[0],
          cluster.properties.location[1],
          mapRef,
          0
        );
        if (_mapsWhenIsShowPoint[0] === IS_SHOW_POINT) {
          return <VectorMapMarker {...basicProps} {...markersSettings} />;
        } else if (_mapsWhenIsShowPoint[1] === IS_SHOW_POINT) {
          return (
            <VectorMapMarker
              {...basicProps}
              {...markersSettings}
              marker={{
                ...cluster.properties,
                location: [cluster.properties.location[0], cluster.properties.location[1] - 360]
              }}
              key={`marker-${cluster.properties.id}-map-1`}
            />
          );
        } else if (_mapsWhenIsShowPoint[2] === IS_SHOW_POINT) {
          return (
            <VectorMapMarker
              {...basicProps}
              {...markersSettings}
              marker={{
                ...cluster.properties,
                location: [cluster.properties.location[0], cluster.properties.location[1] + 360]
              }}
              key={`marker-${cluster.properties.id}-map-2`}
            />
          );
        } else {
          return <VectorMapMarker {...basicProps} {...markersSettings} />;
        }
      }
    }
    return null;
  });
};
